"use client"
import { useEffect, useState } from "react"

export interface PaywallInfo {
  loading:      boolean
  trialActive:  boolean
  trialExpiry:  string | null
  daysToExpiry: number | null   // whole days from today (Harare) to the expiry date
  pricing:      any | null      // effective plan prices (EffectivePricing shape)
}

/** Harare calendar day (UTC+2, no DST) — matches the server's day math. */
function harareToday(): string {
  return new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/**
 * Client hook that reads the public paywall state + effective prices from
 * /api/site-config. Defaults to trial-active while loading so price UI never
 * flashes before the live state is known (matches the server's fail-safe).
 */
export function usePaywall(): PaywallInfo {
  const [info, setInfo] = useState<PaywallInfo>({
    loading: true, trialActive: true, trialExpiry: null, daysToExpiry: null, pricing: null,
  })

  useEffect(() => {
    fetch("/api/site-config")
      .then(r => r.json())
      .then(d => {
        const pw     = d?.paywall
        const expiry = pw?.trialExpiry ?? null
        let days: number | null = null
        if (expiry) {
          const t = Date.parse(harareToday() + "T00:00:00Z")
          const e = Date.parse(expiry + "T00:00:00Z")
          if (!Number.isNaN(e)) days = Math.round((e - t) / 86_400_000)
        }
        setInfo({
          loading:      false,
          trialActive:  pw ? !!pw.isTrialActive : true,
          trialExpiry:  expiry,
          daysToExpiry: days,
          pricing:      d?.pricing ?? null,
        })
      })
      .catch(() => setInfo(i => ({ ...i, loading: false })))
  }, [])

  return info
}

/** Format a YYYY-MM-DD string as e.g. "1 November 2026". */
export function fmtTrialDate(d: string | null): string {
  if (!d) return ""
  const t = Date.parse(d + "T00:00:00Z")
  if (Number.isNaN(t)) return d
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
}
